import { type yup } from '@framework/validation/initYup.ts';
import { useCallback, useMemo } from 'react';
import { type AnyObject } from 'yup';

export interface UseSearchableFormQueryOptions<
  TQuery extends TSearchable,
  TSearchable extends AnyObject = Record<string, any>,
> {
  query: TQuery;
  setQuery: (values: TQuery) => void;
  defaultQuery: TQuery;
  schema: yup.ObjectSchema<TSearchable>;
  transform?: {
    format: (values: TSearchable) => TQuery;
    parse: (values: TQuery) => TSearchable;
  };
  /** Passando `null` si disabilita la paginazione. */
  pageKey: keyof TQuery | null;
  pageDefaultIndex?: number;
  /** Callback alternativa al reset di default (`setQuery({ ...defaultQuery })`). */
  onReset?: () => void;
}

interface UseSearchableFormQueryResult<
  TSearchable extends AnyObject = Record<string, any>,
> {
  handleSearch: (values: TSearchable | null) => void;
  defaultInitialValues: TSearchable;
  parsedQuery: TSearchable;
}

/**
 * Pipeline che lega la `query` (URL state) al `FormHandler` di RHF.
 * `defaultInitialValues` è catturato AL MOUNT (intenzionale): per propagare
 * cambi di `query` post-mount c'è `AdvancedSearchQueryValuesSetter`.
 */
export function useSearchableFormQuery<
  TQuery extends TSearchable,
  TSearchable extends AnyObject = Record<string, any>,
>(
  opts: UseSearchableFormQueryOptions<TQuery, TSearchable>,
): UseSearchableFormQueryResult<TSearchable> {
  const {
    query,
    setQuery,
    defaultQuery,
    schema,
    transform,
    pageKey,
    pageDefaultIndex = 0,
    onReset,
  } = opts;

  const handleSearch = useCallback(
    (values: TSearchable | null) => {
      if (!values) {
        if (onReset) {
          onReset();
        } else {
          setQuery({ ...defaultQuery });
        }
        return;
      }

      const nextQuery = {
        ...(transform?.format ? transform.format(values) : values),
        ...(pageKey ? { [pageKey]: pageDefaultIndex } : {}),
        // Timestamp unico a ogni submit → fa parte della `queryKey` di React
        // Query, quindi forza un refetch anche quando i filtri non cambiano
        // (es. ri-submit dello stesso stato). Effetto collaterale voluto: ogni
        // submit ha una slot di cache dedicata, perciò risposte GET fuori ordine
        // (rimozioni rapide di più filtri) NON sovrascrivono i dati correnti —
        // la risposta vecchia finisce in una slot orfana che nessuno osserva.
        // È escluso dal confronto filtri in `useIsFiltered`.
        ...({ __cacheInvalidator: Date.now() } as any),
      } as TQuery;
      setQuery(nextQuery);
    },
    [defaultQuery, onReset, pageDefaultIndex, pageKey, setQuery, transform],
  );

  // Deps array VUOTO di proposito: vogliamo "congelare" i valori della `query`
  // al mount per inizializzare il form una sola volta. Non è il pattern che
  // usiamo di solito; i cambi di `query` successivi NON devono rigenerare
  // questi initial values (li propaga `AdvancedSearchQueryValuesSetter`).
  // biome-ignore lint/correctness/useExhaustiveDependencies: cattura solo i valori iniziali della query
  const defaultInitialValues = useMemo(() => {
    const parsed = transform?.parse ? transform.parse(query) : query;
    return (schema.cast(parsed, { assert: false }) ?? {}) as TSearchable;
  }, []);

  const parsedQuery = useMemo(() => {
    return transform?.parse ? transform.parse(query) : query;
  }, [query, transform]) as TSearchable;

  return { handleSearch, defaultInitialValues, parsedQuery };
}
